"use client";

import { useTranslations } from "next-intl";
import { GradientDivider } from "@/components/ui/GradientDivider";

const h2 = "font-display text-xl font-bold text-text-primary";
const h3 = "font-display text-xl font-bold text-text-primary mt-4";
const p = "mt-3 text-text-body text-sm leading-relaxed";
const ul =
  "mt-2 space-y-1.5 text-text-body text-sm leading-relaxed list-disc list-inside";

export function PrivacyPolicyPage() {
  const t = useTranslations("Privacy");

  return (
    <section className="pt-8 lg:pt-32 pb-12 lg:pb-20 px-6">
      <div className="mx-auto max-w-3xl">
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-text-primary">
          {t("title")}
        </h1>
        <p className="mt-2 text-text-muted text-sm">{t("lastUpdated")}</p>
        <p className={p}>
          This Privacy Policy describes how Scaffcalc (&ldquo;we&rdquo;,
          &ldquo;us&rdquo;, or &ldquo;our&rdquo;) collects, uses, and shares
          personal information when you use our website, software, and services
          (collectively, the &ldquo;Service&rdquo;). Please read this Privacy
          Policy carefully to understand our practices regarding your personal
          information and how we treat it.
        </p>

        <div className="mt-12 space-y-10">
          {/* 1 */}
          <div>
            <h2 className={h2}>1. Information We Collect</h2>
            <h3 className={h3}>1.1 Personal Information</h3>
            <p className={p}>
              We may collect the following personal information from you when
              you use our Service:
            </p>
            <ul className={ul}>
              <li>Name</li>
              <li>Email address</li>
              <li>Company name</li>
              <li>Job title</li>
              <li>Billing and payment information</li>
              <li>Any other personal information you provide to us</li>
            </ul>
            <h3 className={h3}>1.2 Usage Data and Technical Information</h3>
            <p className={p}>
              When you access and use our Service, we may automatically collect
              certain information about your device, including:
            </p>
            <ul className={ul}>
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Device information</li>
              <li>
                Usage data, such as pages visited, time spent on the Service,
                and other usage statistics
              </li>
            </ul>
          </div>

          {/* 2 */}
          <div>
            <h2 className={h2}>2. How We Use Your Information</h2>
            <p className={p}>We use your personal information to:</p>
            <ul className={ul}>
              <li>Provide, maintain, and improve our Service</li>
              <li>Manage your account and process your payments</li>
              <li>
                Communicate with you about the Service and any updates,
                promotions, or changes
              </li>
              <li>Provide customer support and respond to your inquiries</li>
              <li>Monitor and analyze usage trends to improve our Service</li>
              <li>Ensure the security and integrity of our Service</li>
              <li>Comply with legal obligations and enforce our agreements</li>
            </ul>
          </div>

          {/* 3 */}
          <div>
            <h2 className={h2}>3. How We Share Your Information</h2>
            <p className={p}>
              We do not sell, rent, or share your personal information with
              third parties, except as described in this Privacy Policy. We may
              share your personal information with:
            </p>
            <ul className={ul}>
              <li>
                Service providers and contractors that help us provide and
                maintain our Service, subject to confidentiality agreements
              </li>
              <li>
                Affiliates and subsidiaries under common ownership or control
              </li>
              <li>
                Legal authorities when required by law, such as to comply with a
                subpoena, regulation, or legal request, or to protect the rights
                and safety of ourselves, our users, or others
              </li>
            </ul>
          </div>

          {/* 4 */}
          <div>
            <h2 className={h2}>4. Data Retention</h2>
            <p className={p}>
              We will retain your personal information for as long as necessary
              to provide you with the Service, comply with our legal
              obligations, resolve disputes, and enforce our agreements. We will
              delete your personal information upon your request or when your
              account is terminated, subject to applicable legal retention
              obligations.
            </p>
          </div>

          {/* 5 */}
          <div>
            <h2 className={h2}>5. Data Security</h2>
            <p className={p}>
              We take reasonable measures to protect your personal information
              from unauthorized access, use, or disclosure. However, no method
              of transmission over the Internet or electronic storage is
              completely secure, and we cannot guarantee the absolute security
              of your personal information.
            </p>
          </div>

          {/* 6 */}
          <div>
            <h2 className={h2}>6. Your Rights</h2>
            <p className={p}>
              Under the General Data Protection Regulation (GDPR), you have the
              right to:
            </p>
            <ul className={ul}>
              <li>Access your personal information</li>
              <li>Rectify or update your personal information</li>
              <li>Erase your personal information</li>
              <li>Restrict the processing of your personal information</li>
              <li>Object to the processing of your personal information</li>
              <li>Data portability</li>
            </ul>
            <p className={p}>
              To exercise these rights or if you have any questions or concerns
              about your privacy, please contact us at support@scaffcalc.com.
            </p>
          </div>

          {/* 7 */}
          <div>
            <h2 className={h2}>7. Changes to This Privacy Policy</h2>
            <p className={p}>
              We may update this Privacy Policy from time to time. We will
              notify you of any changes by posting the updated Privacy Policy on
              our website and indicating the &ldquo;Last Updated&rdquo; date.
              Your continued use of the Service after the effective date of any
              changes to this Privacy Policy will be deemed acceptance of those
              changes.
            </p>
          </div>

          {/* 8 */}
          <div>
            <h2 className={h2}>8. Contact Us</h2>
            <p className={p}>
              If you have any questions or concerns about this Privacy Policy or
              our privacy practices, please contact us at:
            </p>
            <p className="mt-3 text-text-primary text-sm font-bold">
              Scaffcalc
            </p>
            <p className="text-text-body text-sm">Kungsgatan 38</p>
            <p className="text-text-body text-sm">support@scaffcalc.com</p>
          </div>

          {/* 9 */}
          <div>
            <h2 className={h2}>9. Governing Law and Jurisdiction</h2>
            <p className={p}>
              This Privacy Policy shall be governed by and construed in
              accordance with the laws of the jurisdiction in which Scaffcalc is
              incorporated, without regard to its conflict of law provisions.
              Any disputes arising out of or related to this Privacy Policy
              shall be subject to the exclusive jurisdiction of the courts
              located within the jurisdiction in which Scaffcalc is
              incorporated.
            </p>
          </div>

          {/* 10 */}
          <div>
            <h2 className={h2}>10. International Data Transfers</h2>
            <p className={p}>
              If you are located in the European Economic Area (EEA) or
              Switzerland, we may transfer your personal information to
              countries outside the EEA or Switzerland, including to the United
              States, for the purposes described in this Privacy Policy. We
              ensure that such transfers are carried out in accordance with
              applicable data protection laws, including by implementing
              appropriate safeguards such as Standard Contractual Clauses
              approved by the European Commission.
            </p>
          </div>

          {/* 11 */}
          <div>
            <h2 className={h2}>11. Third-Party Links</h2>
            <p className={p}>
              Our Service may contain links to third-party websites,
              applications, or services. This Privacy Policy does not apply to
              the information practices of those third parties, and we encourage
              you to review their privacy policies before providing them with
              your personal information.
            </p>
          </div>

          {/* 12 */}
          <div>
            <h2 className={h2}>12. Children&apos;s Privacy</h2>
            <p className={p}>
              Our Service is not directed to or intended for use by individuals
              under the age of 16. We do not knowingly collect or solicit
              personal information from children under the age of 16. If you are
              under 16, please do not use our Service or submit any personal
              information to us. If we become aware that we have collected
              personal information from a child under the age of 16, we will
              take steps to delete such information as soon as possible.
            </p>
          </div>

          {/* 13 */}
          <div>
            <h2 className={h2}>13. Data Protection Officer</h2>
            <p className={p}>
              We have designated a Data Protection Officer (DPO) to oversee our
              privacy and data protection practices. If you have any questions
              or concerns about this Privacy Policy or our handling of your
              personal information, you may contact our DPO at
              support@scaffcalc.com.
            </p>
          </div>

          {/* 14 */}
          <div>
            <h2 className={h2}>14. Complaints</h2>
            <p className={p}>
              If you have a complaint about our privacy practices or believe
              that your personal information has been processed in a manner that
              does not comply with the GDPR, you have the right to lodge a
              complaint with your local data protection authority or with the
              Data Protection Authority of the country where Scaffcalc is
              incorporated.
            </p>
          </div>

          {/* Divider */}
          <GradientDivider />

          {/* Cookie Statement */}
          <div>
            <h2 className="font-display text-2xl font-bold text-text-primary">
              Cookie Statement for Scaffcalc
            </h2>
            <p className="mt-2 text-text-muted text-sm">
              Last updated: March 15, 2023
            </p>
          </div>

          <div>
            <h2 className={h2}>1. Introduction</h2>
            <p className={p}>
              At Scaffcalc, we value your privacy and want to ensure that your
              experience on our website (the &ldquo;Website&rdquo;) is secure
              and enjoyable. This Cookie Statement explains how we use cookies
              and similar technologies on our Website, the types of cookies we
              use, and the choices you have to manage these cookies. By using
              our Website, you consent to our use of cookies as described in
              this Cookie Statement.
            </p>
          </div>

          <div>
            <h2 className={h2}>2. What are cookies?</h2>
            <p className={p}>
              Cookies are small text files that are placed on your computer or
              device by websites that you visit. They are widely used to make
              websites work, or work more efficiently, as well as to provide
              information to the owners of the site. Cookies can be
              &ldquo;persistent&rdquo; or &ldquo;session&rdquo; cookies.
              Persistent cookies remain on your computer when you have gone
              offline, while session cookies are deleted as soon as you close
              your web browser.
            </p>
          </div>

          <div>
            <h2 className={h2}>3. How Scaffcalc uses cookies</h2>
            <p className={p}>
              We use cookies and similar technologies for various purposes,
              including:
            </p>
            <ul className={ul}>
              <li>
                To improve the functionality and performance of our Website
              </li>
              <li>To remember your preferences and settings</li>
              <li>To analyze and measure Website usage and traffic</li>
              <li>To deliver targeted advertising based on your interests</li>
              <li>To support security features</li>
            </ul>
          </div>

          <div>
            <h2 className={h2}>4. Types of cookies we use</h2>
            <p className={p}>
              The following types of cookies may be used on our Website:
            </p>
            <ul className={ul}>
              <li>
                <strong className="text-text-primary">
                  Strictly necessary cookies:
                </strong>{" "}
                These cookies are essential for the operation of our Website.
                They enable you to navigate the site and use its features, such
                as accessing secure areas.
              </li>
              <li>
                <strong className="text-text-primary">
                  Performance cookies:
                </strong>{" "}
                These cookies collect information about how visitors use our
                Website, for instance, which pages visitors go to most often,
                and if they get error messages from web pages. These cookies
                don&apos;t collect information that identifies a visitor. All
                information these cookies collect is aggregated and therefore
                anonymous.
              </li>
              <li>
                <strong className="text-text-primary">
                  Functionality cookies:
                </strong>{" "}
                These cookies allow our Website to remember choices you make and
                provide enhanced, more personal features. They may also be used
                to provide services you have asked for.
              </li>
              <li>
                <strong className="text-text-primary">
                  Targeting or advertising cookies:
                </strong>{" "}
                These cookies are used to deliver adverts more relevant to you
                and your interests. They are also used to limit the number of
                times you see an advertisement as well as help measure the
                effectiveness of the advertising campaign.
              </li>
            </ul>
          </div>

          <div>
            <h2 className={h2}>5. Managing cookies</h2>
            <p className={p}>
              You can manage cookies by adjusting the settings on your browser.
              Most browsers allow you to block cookies or to block cookies from
              particular sites. Browsers can also help you delete cookies when
              you close your browser. However, please note that if you choose to
              disable or block cookies, certain features of our Website may not
              function properly or may be unavailable.
            </p>
          </div>

          <div>
            <h2 className={h2}>6. Changes to this Cookie Statement</h2>
            <p className={p}>
              We may update this Cookie Statement from time to time. When we
              make changes, we will update the &ldquo;Last updated&rdquo; date
              at the top of this page. We encourage you to review this Cookie
              Statement periodically to stay informed about our use of cookies
              and how you can manage them.
            </p>
          </div>

          <div>
            <h2 className={h2}>7. Contact us</h2>
            <p className={p}>
              If you have any questions or concerns about our use of cookies,
              please contact us at support@scaffcalc.com.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
